/**
 * Content tab state. Pure, so the panel can be tested without the editor.
 */

import { WRITING } from './reelCopy';
import { emptyTreatment, type ReelTreatment } from './reelTreatment';

export interface ContentPanelState {
  idea: string;
  status: string;
  treatment: ReelTreatment;
  /** The "Adjust the pictures" section is open. */
  adjustOpen: boolean;
  /** Checkbox inside that section. Off until the person asks the picture to move. */
  movePictures: boolean;
  busy: boolean;
}

export type ContentPanelAction =
  | { type: 'setIdea'; idea: string }
  | { type: 'setLine'; index: number; line: string }
  | { type: 'toggleAdjust' }
  | { type: 'setMovePictures'; movePictures: boolean }
  | { type: 'markWriting' }
  | { type: 'setStatus'; status: string }
  | { type: 'markWritten'; treatment: ReelTreatment }
  | { type: 'markFailed'; message: string };

/** Panel as a new movie opens it: three empty lines, nothing in flight. */
export function initialContentPanelState(): ContentPanelState {
  return {
    idea: '',
    status: '',
    treatment: emptyTreatment(),
    adjustOpen: false,
    movePictures: false,
    busy: false,
  };
}

/** Apply one panel action. Unknown line indexes leave the treatment as it was. */
export function contentPanelReducer(
  state: ContentPanelState,
  action: ContentPanelAction
): ContentPanelState {
  switch (action.type) {
    case 'setIdea':
      return { ...state, idea: action.idea };
    case 'setLine': {
      const beats = state.treatment.beats.map((beat) =>
        beat.index === action.index ? { ...beat, line: action.line } : beat
      );
      if (beats.every((beat, i) => beat === state.treatment.beats[i])) return state;
      return { ...state, treatment: { ...state.treatment, beats } };
    }
    case 'toggleAdjust':
      return { ...state, adjustOpen: !state.adjustOpen };
    case 'setMovePictures':
      return { ...state, movePictures: action.movePictures };
    case 'markWriting':
      return { ...state, busy: true, status: WRITING };
    case 'setStatus':
      return { ...state, status: action.status };
    case 'markWritten':
      return { ...state, busy: false, status: '', treatment: action.treatment };
    case 'markFailed':
      return { ...state, busy: false, status: action.message };
    default:
      return state;
  }
}
